import React, { useEffect, useLayoutEffect, useRef } from "react";
import { canAnimate, clamp, finePointer } from "./motion";

const REVEAL = [
  ".brand-section-head > div",
  ".brand-section-head > p",
  ".brand-mission-benefits > *",
  ".brand-feature-tabs > button",
  ".brand-facet-detail",
  ".brand-product-paths > a",
  ".brand-belief > div:not(.bm-formation-track)",
  ".brand-download-inner > div",
  ".brand-next .brand-container > *",
  ".brand-customers > span",
  ".brand-customers > div > a",
  ".brand-footer-top > div",
  "[data-reveal]",
].join(",");
const GLYPHS = "01234567890/—·+×<>ABCDEFGHJKLMNPRSTUVWXYZ";

function scramble(node) {
  if (node.childNodes.length !== 1 || node.firstChild.nodeType !== 3) return;
  // Write to React's own text node so later renders still land in the DOM.
  const text = node.firstChild;
  const final = text.nodeValue;
  const start = performance.now();
  const tick = (t) => {
    const done = Math.min(1, (t - start) / 720);
    const settled = Math.floor(final.length * done);
    text.nodeValue =
      final.slice(0, settled) +
      final
        .slice(settled)
        .replace(/\S/g, () => GLYPHS[Math.floor(Math.random() * GLYPHS.length)]);
    if (done < 1) requestAnimationFrame(tick);
    else text.nodeValue = final;
  };
  requestAnimationFrame(tick);
}

// Reveals, decoding eyebrows and magnetic buttons for every public page.
export function useSiteMotion() {
  useLayoutEffect(() => {
    if (!canAnimate() || !window.IntersectionObserver) return undefined;
    const root = document.documentElement;
    root.classList.add("motion-ok");
    const nodes = [...document.querySelectorAll(".grupa-site " + REVEAL.split(",").join(", .grupa-site "))];
    const siblings = new Map();
    nodes.forEach((node) => {
      const i = siblings.get(node.parentNode) || 0;
      siblings.set(node.parentNode, i + 1);
      node.setAttribute("data-reveal", "");
      node.style.setProperty("--reveal-i", Math.min(i, 6));
    });
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          observer.unobserve(entry.target);
          entry.target
            .querySelectorAll(":scope > .brand-eyebrow, :scope > span.brand-caption:first-child")
            .forEach(scramble);
          if (entry.target.matches(".brand-eyebrow")) scramble(entry.target);
        }),
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => {
      observer.disconnect();
      nodes.forEach((node) => node.classList.add("is-in"));
      root.classList.remove("motion-ok");
    };
  }, []);
  useEffect(() => {
    if (!canAnimate() || !finePointer()) return undefined;
    let current = null;
    const release = () => {
      current?.style.removeProperty("--mx");
      current?.style.removeProperty("--my");
      current = null;
    };
    const onMove = (e) => {
      const target = e.target.closest?.(".grupa-site .brand-button, .brand-nav-cta");
      if (target !== current) release();
      if (!target) return;
      current = target;
      const rect = target.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
      target.style.setProperty("--mx", (x * 10).toFixed(2) + "px");
      target.style.setProperty("--my", (y * 8).toFixed(2) + "px");
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerleave", release);
    return () => {
      release();
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", release);
    };
  }, []);
  // Headlines lean with scroll speed, so you can feel the page's momentum.
  useEffect(() => {
    if (!canAnimate()) return undefined;
    const root = document.documentElement;
    let last = window.scrollY;
    let skew = 0;
    let raf = 0;
    const tick = () => {
      const y = window.scrollY;
      const v = y - last;
      last = y;
      skew += (clamp(v * 0.09, -2.4, 2.4) - skew) * 0.16;
      if (Math.abs(skew) < 0.01 && !v) {
        raf = 0;
        root.style.removeProperty("--bm-skew");
        return;
      }
      root.style.setProperty("--bm-skew", skew.toFixed(3) + "deg");
      raf = requestAnimationFrame(tick);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      root.style.removeProperty("--bm-skew");
    };
  }, []);
}

// A small navigation arrow that follows your direction, the way Momentum follows the mission,
// leaving a dotted route behind it.
export function WorkerCursor() {
  const node = useRef(null);
  const route = useRef(null);
  useEffect(() => {
    const el = node.current;
    const path = route.current;
    if (!el || !path || !canAnimate() || !finePointer()) return undefined;
    const trail = [];
    const target = { x: -100, y: -100 };
    const pos = { x: -100, y: -100, vx: 0, vy: 0, angle: 0 };
    let raf = 0;
    let shown = false;
    const tick = () => {
      const ax = (target.x - pos.x) * 0.055;
      const ay = (target.y - pos.y) * 0.055;
      pos.vx = (pos.vx + ax) * 0.8;
      pos.vy = (pos.vy + ay) * 0.8;
      pos.x += pos.vx;
      pos.y += pos.vy;
      const speed = Math.hypot(pos.vx, pos.vy);
      if (speed > 0.4) {
        const heading = (Math.atan2(pos.vy, pos.vx) * 180) / Math.PI + 45;
        const delta = ((heading - pos.angle + 540) % 360) - 180;
        pos.angle += delta * 0.18;
      }
      el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) rotate(${pos.angle}deg)`;
      const tip = trail[trail.length - 1];
      if (!tip || Math.hypot(pos.x - tip[0], pos.y - tip[1]) > 3) trail.push([pos.x, pos.y]);
      if (trail.length > 46 || (speed < 0.6 && trail.length)) trail.shift();
      path.setAttribute("d", trail.length > 1 ? "M" + trail.map(([x, y]) => x.toFixed(1) + " " + y.toFixed(1)).join("L") : "");
      raf =
        speed > 0.05 || trail.length > 1 || Math.hypot(target.x - pos.x, target.y - pos.y) > 0.5
          ? requestAnimationFrame(tick)
          : 0;
    };
    const onMove = (e) => {
      target.x = e.clientX + 16;
      target.y = e.clientY + 18;
      if (!shown) {
        pos.x = target.x;
        pos.y = target.y;
        shown = true;
      }
      const busy = e.target.closest?.(
        "a, button, select, input, textarea, .mission-app, .bm-formation.is-leading, .bm-kinetic, .bm-horizon",
      );
      el.classList.toggle("is-busy", !!busy);
      el.classList.add("is-on");
      if (!raf) raf = requestAnimationFrame(tick);
    };
    const onLeave = () => el.classList.remove("is-on");
    document.addEventListener("pointermove", onMove);
    document.documentElement.addEventListener("pointerleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
    };
  }, []);
  return (
    <>
      <svg className="bm-trail" aria-hidden="true">
        <path ref={route} />
      </svg>
      <span ref={node} className="bm-worker" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16">
          <path d="M21 3 3 10.5l7.4 2.1L12.5 20z" fill="#191919" />
        </svg>
      </span>
    </>
  );
}
